import { useEffect, useRef } from "react";
import { useUserStore } from "@/entities/user/model/store";
import { wsManager } from "@/shared/lib/websocket";
import { isAwaitingSubscriptionActivation, isSubscriptionRelatedNotification, registerSubscriptionActivatedHandler, syncSubscriptionAfterPayment, } from "./afterPayment";
import { resolveAccountId } from "./selectors";
/**
 * Держит стор подписки в актуальном состоянии на уровне приложения (монтируется
 * один раз в `App`). Сам стор перечитывается только при монтировании блока
 * «Моя подписка», а узнать об оплате нужно и без перезагрузки страницы:
 *
 *  • возврат во вкладку/миниапп (`visibilitychange`, `focus`), пока ждём
 *    активацию: в миниапе оплата идёт во внешнем браузере, и сигнала об оплате
 *    в приложение не приходит — возврат пользователя и есть этот сигнал;
 *  • WS-уведомление об оплате или подписке (продление, окончание).
 *
 * Сигнал оплаты из вкладки кассы и страница `/payment/success` зовут
 * `syncSubscriptionAfterPayment` сами.
 *
 * `refreshBalance` — обновить баланс: песни по подписке начисляются вместе с
 * её активацией/продлением, позже момента оплаты.
 */
export function useSubscriptionSync(refreshBalance) {
    // Через ref: колбэк у вызывающего пересоздаётся на каждый рендер, а
    // переподписывать слушатели из-за этого незачем.
    const refreshRef = useRef(refreshBalance);
    refreshRef.current = refreshBalance;
    const isAccount = useUserStore((s) => resolveAccountId(s) !== null);
    useEffect(() => {
        registerSubscriptionActivatedHandler(() => void refreshRef.current());
        return () => registerSubscriptionActivatedHandler(null);
    }, []);
    useEffect(() => {
        // Гостю и неавторизованному подписка недоступна — и WS ради неё не поднимаем.
        if (!isAccount)
            return;
        const onReturn = () => {
            if (document.visibilityState !== "visible")
                return;
            if (!isAwaitingSubscriptionActivation())
                return;
            syncSubscriptionAfterPayment({ expectSubscription: true });
        };
        document.addEventListener("visibilitychange", onReturn);
        window.addEventListener("focus", onReturn);
        const disposeWs = wsManager.subscribeAll((notification, meta) => {
            // Стартовый бэклог — старые события, по ним перечитывать нечего.
            if (meta.isBacklog)
                return;
            if (!isSubscriptionRelatedNotification(notification.payload?.type))
                return;
            // Продление/окончание меняют и подписочный баланс песен.
            void refreshRef.current();
            syncSubscriptionAfterPayment();
        });
        return () => {
            document.removeEventListener("visibilitychange", onReturn);
            window.removeEventListener("focus", onReturn);
            disposeWs();
        };
    }, [isAccount]);
}
